import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Renders a brand title (HTML) and a subtitle below it. The subtitle is
 * forced to the EXACT pixel width of the title using JS measurement +
 * `text-align-last: justify` to spread its words across the full width.
 *
 * This is the most reliable cross-browser technique: when subtitle is
 * naturally narrower than the title (our case), the browser distributes
 * the extra space between words, producing edge-to-edge alignment.
 */
const useIsoLayout =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export const BrandWordmark = ({
  title,
  subtitle,
  titleClassName = "",
  subtitleClassName = "",
  titleStyle = {},
  subtitleStyle = {},
  containerClassName = "",
}) => {
  const titleRef = useRef(null);
  const [titleWidth, setTitleWidth] = useState(0);

  useIsoLayout(() => {
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const tEl = titleRef.current;
      if (!tEl) return;
      const w = tEl.getBoundingClientRect().width;
      if (w > 4) setTitleWidth(w);
    };
    measure();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() =>
        requestAnimationFrame(() => requestAnimationFrame(measure))
      );
    }
    const ro = new ResizeObserver(measure);
    if (titleRef.current) ro.observe(titleRef.current);
    window.addEventListener("resize", measure);
    const timers = [50, 200, 600, 1500].map((ms) => setTimeout(measure, ms));
    return () => {
      cancelled = true;
      ro.disconnect();
      window.removeEventListener("resize", measure);
      timers.forEach(clearTimeout);
    };
  }, [title, subtitle]);

  return (
    <div className={containerClassName} style={{ position: "relative" }}>
      <span
        ref={titleRef}
        className={titleClassName}
        style={{
          display: "block",
          whiteSpace: "nowrap",
          ...titleStyle,
        }}
      >
        {title}
      </span>

      <span
        className={subtitleClassName}
        style={{
          display: "block",
          ...subtitleStyle,
          ...(titleWidth > 0
            ? {
                width: `${titleWidth}px`,
                textAlign: "justify",
                textAlignLast: "justify",
                MozTextAlignLast: "justify",
              }
            : { visibility: "hidden", whiteSpace: "nowrap" }),
        }}
      >
        {subtitle}
      </span>
    </div>
  );
};

export default BrandWordmark;
